import { createEvent, restore, sample } from 'effector'

import { fx } from '../../../../libs'
import { $params } from '../../../../features/router'
import { Permission, PermissionsResponse, Role } from './types'

export const roleMounted = createEvent()
export const attachPermissionMounted = createEvent<{
  permissionId: string
  roleId: string
}>()
export const detachPermissionMounted = createEvent<{
  permissionId: string
  roleId: string
}>()

export const getRole = fx<void, Role>({
  method: 'GET',
  url: `/role/{roleId}`,
})

export const getInheritedRoles = fx<void, string[]>({
  method: 'GET',
  url: `/role/{roleId}/inherited`,
})

export const getRolePermissions = fx<void, PermissionsResponse>({
  method: 'GET',
  url: `/permission/by_role/{roleId}`,
})

export const getAllPermissions = fx<void, PermissionsResponse>({
  method: 'GET',
  url: `/permission`,
})

export const attachPermission = fx<{ role: string }, void>({
  method: 'POST',
  url: `/permission/{permissionId}/attach_role`,
})

export const detachPermission = fx<{ role: string }, void>({
  method: 'POST',
  url: `/permission/{permissionId}/detach_role`,
})

export const $role = restore<Role>(
  getRole.doneData.map((a) => a.body),
  { id: '', description: '' },
)

export const $inheritedRoles = restore<string[]>(
  getInheritedRoles.doneData.map((a) => (Array.isArray(a.body) ? a.body : [])),
  [],
)

export const $rolePermissions = restore<Permission[]>(
  getRolePermissions.doneData.map((a) => a.body.items || []),
  [],
)

export const $allPermissions = restore<Permission[]>(
  getAllPermissions.doneData.map((a) => a.body.items || []),
  [],
)

sample({
  clock: roleMounted,
  source: $params,
  fn: (params) => {
    const id = params?.roleID ?? ''
    return {
      url: `/role/${id}`,
      query: {
        populate: JSON.stringify(['Role.userGroups']),
      },
    }
  },
  target: getRole,
})

sample({
  clock: roleMounted,
  source: $params,
  fn: (params) => {
    const id = params?.roleID ?? ''
    return { url: `/role/${id}/inherited` }
  },
  target: getInheritedRoles,
})

sample({
  clock: roleMounted,
  source: $params,
  fn: (params) => {
    const id = params?.roleID ?? ''
    return { url: `/permission/by_role/${id}` }
  },
  target: getRolePermissions,
})

sample({
  clock: roleMounted,
  fn: () => ({
    query: { limit: '1000', page: '1' },
  }),
  target: getAllPermissions,
})

sample({
  clock: attachPermissionMounted,
  fn: ({ permissionId, roleId }) => ({
    url: `/permission/${permissionId}/attach_role`,
    body: { role: roleId },
  }),
  target: attachPermission,
})

sample({
  clock: detachPermissionMounted,
  fn: ({ permissionId, roleId }) => ({
    url: `/permission/${permissionId}/detach_role`,
    body: { role: roleId },
  }),
  target: detachPermission,
})

sample({
  clock: [attachPermission.done, detachPermission.done],
  source: $params,
  fn: (params) => {
    const id = params?.roleID ?? ''
    return { url: `/permission/by_role/${id}` }
  },
  target: getRolePermissions,
})
